// src/App.js
import React from 'react';
import { 
  Container, 
  Grid, 
  Typography, 
  Button, 
  Box, 
  Card, 
  CardContent, 
  Divider,
  Paper,
  Avatar,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Flight as FlightIcon,
  Work as WorkIcon,
  Build as BuildIcon,
  School as SchoolIcon,
  ShoppingCart as ShoppingCartIcon,
  CameraAlt as CameraIcon,
  Agriculture as AgricultureIcon,
  Map as MapIcon,
  Security as SecurityIcon
} from '@mui/icons-material';

const FeatureCard = ({ icon, title, description }) => {
  return (
    <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 3 }}>
      <CardContent sx={{ textAlign: 'center', p: 3 }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 60, height: 60, mb: 2, mx: 'auto' }}>
          {icon}
        </Avatar>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {description}
        </Typography>
      </CardContent>
    </Card>
  );
};

const ServiceItem = ({ icon, title, items }) => {
  return (
    <Paper elevation={0} sx={{ p: 3, height: '100%', border: '1px solid #e0e0e0', borderRadius: 2 }}>
      <Box display="flex" alignItems="center" mb={2}>
        {icon}
        <Typography variant="h6" component="h3" sx={{ ml: 1, fontWeight: 'bold' }}>
          {title}
        </Typography>
      </Box>
      <ul style={{ paddingLeft: 20, margin: 0 }}>
        {items.map((item, index) => (
          <li key={index}>
            <Typography variant="body1" paragraph sx={{ mb: 1 }}>
              {item}
            </Typography>
          </li>
        ))}
      </ul>
    </Paper>
  );
};

const App = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <div>
      {/* Hero Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #3a7bd5 0%, #00d2ff 100%)',
        color: 'white',
        py: 10,
        position: 'relative',
        overflow: 'hidden',
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(https://images.unsplash.com/photo-1507582020474-9a35b7d455d9?auto=format&fit=crop&w=1350&q=80)',
          backgroundSize: 'cover',
          opacity: 0.2,
          zIndex: 0
        }
      }}>
        <Container sx={{ position: 'relative', zIndex: 1 }}>
          <Typography 
            variant={isMobile ? 'h3' : 'h2'} 
            component="h1" 
            gutterBottom 
            align="center"
            sx={{ 
              fontWeight: 'bold',
              textShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}
          >
            Welcome to Drone Planet
          </Typography>
          <Typography 
            variant={isMobile ? 'h6' : 'h5'} 
            align="center" 
            paragraph
            sx={{ 
              maxWidth: 800,
              mx: 'auto',
              mb: 4,
              textShadow: '0 1px 2px rgba(0,0,0,0.2)'
            }}
          >
            Your all-in-one hub for drones, accessories, jobs, and training. Serving enthusiasts, professionals, and businesses across industries.
          </Typography>
          <Box display="flex" justifyContent="center" gap={2}>
            <Button 
              variant="contained" 
              color="secondary" 
              size="large"
              sx={{ 
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 'bold',
                boxShadow: 3
              }}
            >
              Explore Now
            </Button>
            <Button 
              variant="outlined" 
              color="inherit"
              size="large"
              sx={{ 
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 'bold',
                borderWidth: 2,
                '&:hover': { borderWidth: 2 }
              }}
            >
              Learn More
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: 8, bgcolor: 'background.paper' }}>
        <Container>
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            align="center"
            sx={{ 
              fontWeight: 'bold',
              mb: 6,
              position: 'relative',
              '&:after': {
                content: '""',
                position: 'absolute',
                bottom: -10,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 80,
                height: 4,
                bgcolor: 'primary.main',
                borderRadius: 2
              }
            }}
          >
            Why Choose Drone Planet?
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={3}>
              <FeatureCard
                icon={<FlightIcon fontSize="large" />}
                title="Premium Drones"
                description="Top-quality drones from leading manufacturers at competitive prices."
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FeatureCard
                icon={<WorkIcon fontSize="large" />}
                title="Career Opportunities"
                description="Find your dream job in the rapidly growing drone industry."
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FeatureCard
                icon={<BuildIcon fontSize="large" />}
                title="Professional Services"
                description="On-demand drone services for businesses and individuals."
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FeatureCard
                icon={<SchoolIcon fontSize="large" />}
                title="Expert Training"
                description="DGCA-approved courses to launch your drone career."
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Drone Jobs & Career Opportunities */}
      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container>
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            sx={{ 
              fontWeight: 'bold',
              mb: 6,
              position: 'relative',
              '&:after': {
                content: '""',
                position: 'absolute',
                bottom: -10,
                left: 0,
                width: 80,
                height: 4,
                bgcolor: 'secondary.main',
                borderRadius: 2
              }
            }}
          >
            Drone Jobs & Career Opportunities
          </Typography>
          <Typography variant="h6" paragraph sx={{ mb: 6, maxWidth: 800 }}>
            Find Top Drone Jobs in India. Drone Planet connects job seekers with leading drone industry employers. Whether you're a drone pilot, GIS analyst, surveyor, or technician, we help you find opportunities that match your skills.
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <ServiceItem
                icon={<WorkIcon color="primary" fontSize="large" />}
                title="For Job Seekers:"
                items={[
                  "Wide Range of Jobs: Explore roles in surveying, mapping, agriculture, security, and inspections.",
                  "Easy Application: Apply with ease and connect with recruiters in just a few clicks.",
                  "Nationwide Opportunities: Find jobs across India, including remote options.",
                  "Career Growth & Certification: Access training programs and internships to enhance your career."
                ]}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ServiceItem
                icon={<BuildIcon color="primary" fontSize="large" />}
                title="For Employers:"
                items={[
                  "Post Jobs: Reach qualified candidates in the drone industry.",
                  "Hire Skilled Talent: Find DGCA-certified pilots, GIS analysts, and more.",
                  "Efficient Recruitment: Simplify hiring with automated candidate matching.",
                  "Industry-Specific Talent Pool: Access specialized professionals in various sectors."
                ]}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Drone as a Service (DaaS) */}
      <Box sx={{ py: 8, bgcolor: 'background.paper' }}>
        <Container>
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            sx={{ 
              fontWeight: 'bold',
              mb: 6,
              position: 'relative',
              '&:after': {
                content: '""',
                position: 'absolute',
                bottom: -10,
                left: 0,
                width: 80,
                height: 4,
                bgcolor: 'primary.main',
                borderRadius: 2
              }
            }}
          >
            Drone as a Service (DaaS)
          </Typography>
          <Typography variant="h6" paragraph sx={{ mb: 6, maxWidth: 800 }}>
            Hire Certified Drone Professionals for Your Business. Drone Planet offers on-demand aerial solutions with DGCA-certified pilots and technicians for industries like construction, agriculture, and media.
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <ServiceItem
                icon={<CameraIcon color="primary" fontSize="large" />}
                title="Our Services:"
                items={[
                  "Surveying & Mapping: High-precision GIS mapping and 3D modeling.",
                  "Aerial Photography & Videography: Stunning visuals for real estate, media, and events.",
                  "Inspection & Surveillance: Remote monitoring and infrastructure inspections.",
                  "Agriculture Solutions: Precision spraying, crop monitoring, and yield optimization."
                ]}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ServiceItem
                icon={<FlightIcon color="primary" fontSize="large" />}
                title="Why Choose Us?"
                items={[
                  "Expert Network: Access certified drone professionals.",
                  "Flexible Hiring: Choose from daily, project, or long-term contracts.",
                  "Cost-Effective: Save on equipment and training costs.",
                  "Easy Booking: Seamless process to book services based on your needs."
                ]}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* RPTOs – Drone Training & Certification */}
      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container>
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            sx={{ 
              fontWeight: 'bold',
              mb: 6,
              position: 'relative',
              '&:after': {
                content: '""',
                position: 'absolute',
                bottom: -10,
                left: 0,
                width: 80,
                height: 4,
                bgcolor: 'secondary.main',
                borderRadius: 2
              }
            }}
          >
            Drone Training & Certification
          </Typography>
          <Typography variant="h6" paragraph sx={{ mb: 6, maxWidth: 800 }}>
            Build a Career in the Drone Industry. Drone Planet partners with DGCA-approved Remote Pilot Training Organizations (RPTOs) to offer specialized training courses, providing hands-on learning and expert guidance.
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <ServiceItem
                icon={<SchoolIcon color="primary" fontSize="large" />}
                title="Courses Offered:"
                items={[
                  "Drone Pilot Training: Learn drone operation, safety, regulations, and flight planning.",
                  "Kisan Drone Operator: Master AI-driven drones for agriculture, including spraying and crop analysis.",
                  "Drone Manufacturing & Assembly: Learn to build, repair, and assemble drones for commercial use.",
                  "DGPS Surveying & GIS Mapping: Train in land mapping, surveys, and geospatial analysis with drones."
                ]}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ServiceItem
                icon={<WorkIcon color="primary" fontSize="large" />}
                title="Why Train With Us?"
                items={[
                  "Industry-Relevant Curriculum: Learn from certified trainers with real-world experience.",
                  "Hands-On Learning: Gain practical experience with live flights and mission planning.",
                  "Job Support: Get placement assistance and career guidance after completing the course."
                ]}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Buy Drones & Accessories */}
      <Box sx={{ py: 8, bgcolor: 'background.paper' }}>
        <Container>
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            sx={{ 
              fontWeight: 'bold',
              mb: 6,
              position: 'relative',
              '&:after': {
                content: '""',
                position: 'absolute',
                bottom: -10,
                left: 0,
                width: 80,
                height: 4,
                bgcolor: 'primary.main',
                borderRadius: 2
              }
            }}
          >
            Buy Drones & Accessories
          </Typography>
          <Typography variant="h6" paragraph sx={{ mb: 6, maxWidth: 800 }}>
            Find the Best Drones at Competitive Prices. Looking for drones, parts, or accessories? We connect you with top manufacturers and verified vendors, offering quality products at competitive prices for amateurs, content creators, and professionals.
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <ServiceItem
                icon={<ShoppingCartIcon color="primary" fontSize="large" />}
                title="Why Buy From Us?"
                items={[
                  "Wide Selection: Choose from top brands and specialized drones for various uses.",
                  "High-Quality Accessories: Batteries, propellers, cameras, GPS modules, and more.",
                  "Trusted Vendors: Purchase from authorized, reliable sellers.",
                  "Competitive Pricing & Deals: Discounts, bundles, and seasonal offers.",
                  "Seamless Shopping: Easy, secure purchasing with fast shipping."
                ]}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ServiceItem
                icon={<BuildIcon color="primary" fontSize="large" />}
                title="Are You a Vendor?"
                items={[
                  "Expand Your Business: Reach targeted customers across industries.",
                  "Hassle-Free Selling: Simple onboarding and product listing.",
                  "Dedicated Support: Get help with listings, orders, and marketing.",
                  "Grow Your Brand: Boost visibility and sales through our platform."
                ]}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Call to Action */}
      <Box sx={{ 
        py: 10, 
        background: 'linear-gradient(to right, #3a7bd5, #00d2ff)',
        color: 'white',
        textAlign: 'center'
      }}>
        <Container>
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
            Ready to Elevate Your Drone Experience?
          </Typography>
          <Typography variant="h6" paragraph sx={{ maxWidth: 700, mx: 'auto', mb: 4 }}>
            Join thousands of drone enthusiasts, professionals, and businesses who trust Drone Planet for all their drone needs.
          </Typography>
          <Button 
            variant="contained" 
            color="secondary" 
            size="large"
            sx={{ 
              px: 6,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 'bold',
              fontSize: '1.1rem',
              boxShadow: 3
            }}
          >
            Get Started Today
          </Button>
        </Container>
      </Box>
    </div>
  );
};

export default App;